/*
 * Vehicle Sharing Management Component
 * 
 * Displays list of users a vehicle is shared with
 * Features:
 * - View all shared users for a vehicle
 * - Edit sharing schedules and settings
 * - Temporarily disable/enable sharing
 * - Remove sharing access
 * - Visual indicators for sharing status
 * 
 * For vehicle owners to manage their sharing permissions
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';
import VehicleShareForm from './VehicleShareForm';

/**
 * Schedule Display Component
 */
const ScheduleDisplay = ({ schedule, shareAtAllTimes }) => {
  if (shareAtAllTimes) {
    return (
      <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
        🕐 All Times
      </span>
    );
  }

  // Handle both old and new schedule formats
  const activeDays = Object.keys(schedule).filter(day => {
    const daySchedule = schedule[day];
    // New format: check if enabled and has time_ranges
    if (daySchedule && typeof daySchedule === 'object' && 'enabled' in daySchedule) {
      return daySchedule.enabled && daySchedule.time_ranges && daySchedule.time_ranges.length > 0;
    }
    // Old format: check if day has start_time and end_time
    return daySchedule && daySchedule.start_time && daySchedule.end_time;
  });
  
  if (activeDays.length === 0) {
    return (
      <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
        📅 No Schedule
      </span>
    );
  }

  const dayAbbrevs = {
    monday: 'Mon',
    tuesday: 'Tue', 
    wednesday: 'Wed',
    thursday: 'Thu',
    friday: 'Fri',
    saturday: 'Sat',
    sunday: 'Sun'
  };

  return (
    <div className="space-y-1">
      {activeDays.slice(0, 3).map(day => {
        const daySchedule = schedule[day];
        return (
          <div key={day} className="text-xs text-gray-600">
            <span className="font-medium">{dayAbbrevs[day]}:</span>{' '}
            {/* Handle new format with time_ranges */}
            {daySchedule.time_ranges && daySchedule.time_ranges.length > 0 ? (
              <span>
                {daySchedule.time_ranges.map((range, index) => (
                  <span key={index}>
                    {range.start_time}-{range.end_time}
                    {index < daySchedule.time_ranges.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </span>
            ) : (
              /* Handle old format */
              <span>{daySchedule.start_time} - {daySchedule.end_time}</span>
            )}
          </div>
        );
      })}
      {activeDays.length > 3 && (
        <div className="text-xs text-gray-500">
          +{activeDays.length - 3} more days
        </div>
      )}
    </div>
  );
};

/**
 * Share Item Component
 */
const ShareItem = ({ share, onUpdate, onRemove, onToggleDisable }) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleToggleDisable = async () => {
    try {
      await onToggleDisable(share.id);
    } catch (error) {
    }
  };

  const handleRemove = async () => {
    if (window.confirm('Are you sure you want to remove sharing access for this user?')) {
      try {
        await onRemove(share.id);
      } catch (error) {
      }
    }
  };

  return (
    <>
      <div className={`border rounded-lg p-4 ${
        share.temporarily_disabled ? 'bg-gray-50 border-gray-300' : 'bg-white border-gray-200'
      }`}>
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-2 flex-wrap">
                <h4 className="font-medium text-gray-900">{share.shared_with_email}</h4>
                {share.temporarily_disabled && (
                  <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">
                    🚫 Disabled
                  </span>
                )}
                {!share.temporarily_disabled && (
                  <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    ✅ Active
                  </span>
                )}
              </div>
              
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Schedule:</span>
                  <div className="mt-1">
                    <ScheduleDisplay 
                      schedule={share.schedule || {}} 
                      shareAtAllTimes={share.share_at_all_times}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-4 text-xs text-gray-500 flex-wrap">
                  <span>Shared: {new Date(share.created_at).toLocaleDateString()}</span>
                  {share.send_notification && (
                    <span className="flex items-center">
                      <span className="mr-1">📧</span>
                      Notified
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
            <button
              onClick={() => setIsEditing(true)}
              className="flex-1 min-w-0 px-3 py-2 bg-blue-100 text-blue-800 text-xs rounded-md hover:bg-blue-200 transition-colors"
            >
              Edit
            </button>
            <button
              onClick={handleToggleDisable}
              className={`flex-1 min-w-0 px-3 py-2 text-xs rounded-md transition-colors ${
                share.temporarily_disabled
                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                  : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
              }`}
            >
              {share.temporarily_disabled ? 'Enable' : 'Disable'}
            </button>
            <button
              onClick={handleRemove}
              className="flex-1 min-w-0 px-3 py-2 bg-red-100 text-red-800 text-xs rounded-md hover:bg-red-200 transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {isEditing && (
        <VehicleShareForm
          vehicle={share.vehicle_details}
          existingShare={share}
          onClose={() => setIsEditing(false)}
          onSuccess={(updatedShare) => {
            setIsEditing(false);
            onUpdate(updatedShare);
          }}
          mode="edit"
        />
      )}
    </>
  );
};

/**
 * Main Vehicle Sharing Management Component
 */
const VehicleShareManagement = ({ vehicle, onClose, onUpdate }) => {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showShareForm, setShowShareForm] = useState(false);

  useEffect(() => {
    loadShares();
  }, [vehicle.id]);

  const loadShares = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/vehicles/${vehicle.id}/shares`);
      setShares(response.data.shares);
    } catch (error) {
      setError('Failed to load sharing information');
    } finally {
      setLoading(false);
    }
  };

  const handleNewShare = (newShare) => {
    setShares([...shares, newShare.share]);
    setShowShareForm(false);
    // Notify parent component to refresh share counts
    if (onUpdate) onUpdate();
  };

  const handleUpdateShare = async (updatedShareResponse) => {
    // Instead of updating local state, reload all shares to get the latest data
    await loadShares();
    // Notify parent component to refresh share counts
    if (onUpdate) onUpdate();
  };

  const handleToggleDisable = async (shareId) => {
    try {
      const response = await axios.put(`${API}/vehicles/shares/${shareId}/toggle-disable`);
      setShares(shares.map(share => 
        share.id === shareId 
          ? { ...share, temporarily_disabled: response.data.temporarily_disabled }
          : share
      ));
      // Notify parent component to refresh share counts
      if (onUpdate) onUpdate();
    } catch (error) {
      throw error;
    }
  };

  const handleRemoveShare = async (shareId) => {
    try {
      await axios.delete(`${API}/vehicles/shares/${shareId}`);
      setShares(shares.filter(share => share.id !== shareId));
      // Notify parent component to refresh share counts
      if (onUpdate) onUpdate();
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="mr-3">👥</span>
                Shared With
              </h2>
              <p className="text-gray-600 mt-1">
                Manage sharing for {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.license_plate})
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowShareForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                + Share with New User
              </button>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">⏳</div>
              <p className="text-gray-600">Loading sharing information...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              {shares.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 text-6xl mb-4">👥</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Sharing Set Up</h3>
                  <p className="text-gray-600 mb-6">
                    This vehicle isn't shared with anyone yet. Click "Share with New User" to start sharing.
                  </p>
                  <button
                    onClick={() => setShowShareForm(true)}
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Share with New User
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900">
                      Shared with {shares.length} user{shares.length !== 1 ? 's' : ''}
                    </h3>
                    <div className="text-sm text-gray-500">
                      {shares.filter(s => !s.temporarily_disabled).length} active, {' '}
                      {shares.filter(s => s.temporarily_disabled).length} disabled
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {shares.map(share => (
                      <ShareItem
                        key={share.id}
                        share={share}
                        onUpdate={handleUpdateShare}
                        onRemove={handleRemoveShare}
                        onToggleDisable={handleToggleDisable}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Share Form Modal */}
      {showShareForm && (
        <VehicleShareForm
          vehicle={vehicle}
          onClose={() => setShowShareForm(false)}
          onSuccess={handleNewShare}
        />
      )}
    </div>
  );
};

export default VehicleShareManagement;